/* Version: 3.5.155 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import 'react';
import { usePreventScroll, OverlayContainer, FocusScope } from 'react-aria';
import PropTypes from 'prop-types';
import Icon from '../Icon/Icon.js';
import i18n from '../../lang/i18n/index.js';
import ThemeProvider from '../../theme/ThemeProvider.js';
import { safeRest, rem, noop } from '../../utils/functions.js';
import handle from '../../utils/handle.js';
import ModalContainer from './ModalContainer.js';
import { CloseButtonWrapper, CloseButton } from './styledComponents.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';
import useLang from '../../hooks/useLang.js';
import useTheme from '../../hooks/useTheme.js';

var _excluded = ["id", "role", "isOpen", "onClose", "closeButtonAriaLabel", "showCloseButton", "shouldCloseOnBackdropClick", "shouldCloseWithEscapeKey", "customStyle", "children", "ariaLabel", "ariaLabelledBy", "ariaDescribedBy"];
var Modal = function Modal(_ref) {
  var controlledId = _ref.id,
    _ref$role = _ref.role,
    role = _ref$role === undefined ? 'dialog' : _ref$role,
    _ref$isOpen = _ref.isOpen,
    isOpen = _ref$isOpen === undefined ? false : _ref$isOpen,
    _ref$onClose = _ref.onClose,
    onClose = _ref$onClose === undefined ? noop : _ref$onClose,
    closeButtonAriaLabel = _ref.closeButtonAriaLabel,
    _ref$showCloseButton = _ref.showCloseButton,
    showCloseButton = _ref$showCloseButton === undefined ? true : _ref$showCloseButton,
    _ref$shouldCloseOnBac = _ref.shouldCloseOnBackdropClick,
    shouldCloseOnBackdropClick = _ref$shouldCloseOnBac === undefined ? true : _ref$shouldCloseOnBac,
    _ref$shouldCloseWithE = _ref.shouldCloseWithEscapeKey,
    shouldCloseWithEscapeKey = _ref$shouldCloseWithE === undefined ? true : _ref$shouldCloseWithE,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      modalStyle: {}
    } : _ref$customStyle,
    children = _ref.children,
    ariaLabel = _ref.ariaLabel,
    ariaLabelledBy = _ref.ariaLabelledBy,
    ariaDescribedBy = _ref.ariaDescribedBy,
    rest = _objectWithoutProperties(_ref, _excluded);
  var safeProps = safeRest(rest);
  var _useDerivedState = useDerivedState(useId('modal-id-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var lang = useLang();
  var theme = useTheme();
  var wrappedOnClose = handle(onClose);
  usePreventScroll({
    isDisabled: !isOpen
  });
  return isOpen && /*#__PURE__*/jsx(OverlayContainer, {
    children: /*#__PURE__*/jsx(ThemeProvider, {
      theme: theme,
      children: /*#__PURE__*/jsx(FocusScope, {
        contain: true,
        autoFocus: true,
        restoreFocus: true,
        children: /*#__PURE__*/jsxs(ModalContainer, _objectSpread(_objectSpread({
          id: id,
          "data-testid": "modal-test",
          isOpen: isOpen,
          onClose: wrappedOnClose,
          isKeyboardDismissDisabled: !shouldCloseWithEscapeKey,
          isDismissable: shouldCloseOnBackdropClick,
          customStyle: customStyle,
          ariaLabel: ariaLabel,
          ariaLabelledBy: ariaLabelledBy,
          ariaDescribedBy: ariaDescribedBy,
          role: role
        }, safeProps), {}, {
          children: [showCloseButton && /*#__PURE__*/jsx(CloseButtonWrapper, {
            children: /*#__PURE__*/jsx(CloseButton, {
              id: "".concat(id, "-close-button"),
              "aria-label": closeButtonAriaLabel || i18n[lang].modal.closeButtonAriaLabelDefault,
              "data-testid": "closeButton",
              onClick: wrappedOnClose,
              children: /*#__PURE__*/jsx(Icon, {
                width: rem(15),
                height: rem(15),
                manulifeName: "close"
              })
            })
          }), children]
        }))
      })
    })
  });
};
process.env.NODE_ENV !== "production" ? Modal.propTypes = {
  /** ID is required for Automated Testing, Google analytics and Google Tag Manager */
  id: PropTypes.string,
  /**
   * String indicating the role of the modal, allowing roles such as "alertdialog" to be applied.
   * This attribute is "dialog" by default.
   */
  role: PropTypes.string,
  /** aria-label attributes for Modal Content. Dialog modal should have either this or aria-labelledby. */
  ariaLabel: PropTypes.string,
  /** aria-labelledby that refers to a visible dialog title. Dialog modal should have either this or aria-label.
   * NOTE that this will override aria-label if both are supplied. */
  ariaLabelledBy: PropTypes.string,
  /**
   * aria-describedby is optional. It should be set on element that
   * describes the primary purpose or message of the dialog
   */
  ariaDescribedBy: PropTypes.string,
  /** closeButtonAriaLabel is the aria-label for close button on the top right */
  closeButtonAriaLabel: PropTypes.string,
  /** Indicate whether the modal is displayed */
  isOpen: PropTypes.bool,
  /** onClick handler for optional close button, backdrop and ESC key */
  onClose: PropTypes.func,
  /** Indicate Modal should show optional close button */
  showCloseButton: PropTypes.bool,
  /** Indicate if clicking on back drop should close the modal or not */
  shouldCloseOnBackdropClick: PropTypes.bool,
  /** Indicate if pressing on escape key should close the modal or not */
  shouldCloseWithEscapeKey: PropTypes.bool,
  /** Content of Modal */
  children: PropTypes.node,
  /** Style override */
  customStyle: PropTypes.shape({
    modalStyle: PropTypes.object
  })
} : undefined;
process.env.NODE_ENV !== "production" ? Modal.displayName = 'Modal' : undefined;

export { Modal as default };
